/**
 * 帮助中心结构化文档。每个 section 一个 markdown 字符串。
 * 渲染由 HelpPage 负责，左侧目录用 sections 数组生成。
 */

export interface HelpSection {
  id: string;
  title: string;
  group: '快速开始' | '集成工作流' | '核心概念' | '常见问题' | '参考';
  /** Markdown 内容 */
  content: string;
}

export const HELP_DOCS: HelpSection[] = [
  // ==================== 快速开始 ====================
  {
    id: 'quickstart',
    title: '5 分钟新建一所学校',
    group: '快速开始',
    content: `
# 5 分钟新建一所学校

如果你刚拿到平台管理员账号，按下面顺序操作就能快速搭起一所完整学校：

1. **进入「学校管理」** → 新建学校（填写名称、地址等）
2. **进入老师管理** → 「批量导入」上传老师 Excel
3. **进入学校班级页** → 「批量创建」按年级生成班级
4. **回老师管理** → 编辑老师，设置「校管理员」或「班主任」权限
5. **进入学生管理** → 「批量导入」上传学生 Excel

完成后，校管理员就可以登录用户端的「学校管理」自行运营本校。

> 详细步骤见下方「集成工作流」中的相应章节。
`.trim(),
  },

  // ==================== 集成工作流 ====================
  {
    id: 'workflow-school-setup',
    title: '工作流 1：学校初始化',
    group: '集成工作流',
    content: `
# 工作流 1：学校初始化

**目标**：从零搭建一所学校，包含老师、班级、学生，以及权限分配。

## 适合的角色

- **平台管理员（admin）**：跨学校的运营方
- **校管理员**：登录用户端 → 「学校管理」执行同样流程（已默认锁定到自己学校）

## 步骤

### 1. 创建学校

进入「学校管理」→「新建学校」，填写学校基本信息。

> 校管理员跳过这一步，直接从第 2 步开始（学校已绑定）。

### 2. 批量导入老师

进入「学校 → 教师管理」→「批量导入」按钮，上传 Excel 表。

**Excel 表头自动识别（列顺序任意）**：

| 列 | 是否必填 | 说明 |
|---|---|---|
| 身份证号 | ✅ | 18 位身份证 |
| 姓名 | ✅ |  |
| 工号 | ❌ |  |
| 部门 | ❌ |  |
| 职位 | ❌ |  |
| 电话 | ❌ |  |
| 校管理员 | ❌ | 填 "是 / Y / true" 标记为校管理员 |
| 班级 | ❌ | 多个班级用 \`;\` 或 \`,\` 分隔，先批量建班再填会更方便 |

**初始密码**：身份证号后 6 位。

**已存在身份证 → 跳过不覆盖**。

### 3. 批量创建班级

进入「班级管理」→「批量创建」，可以两种方式：

- **模板批量**：例如 \`高一{n}班\`，起始 1，个数 12 → 一次创建 12 个班
- **班名列表**：每行一个班名（如 国际班 / 实验班 / 强基班）

注意：批量创建**不支持同时分配班主任**，需要在班级建好后单独编辑。

### 4. 设置班主任

进入班级编辑，「班主任」字段是搜索式下拉，输入老师姓名/工号，选中后保存。

> 设置班主任后，该老师**自动获得本班的查看/管理权限**（写入 \`teacher_class_permissions\`）。

### 5. 设置校管理员

进入「教师管理」→ 找到对应老师 → 编辑 →「校管理员」开关。

> 校管理员可以登录用户端，进入「学校管理」管理本校所有班级、学生、教师，并查看本校所有问卷数据 / 危机预警 / 大报告。
> 校管理员**不能改其他人的「校管理员」标记**（避免权限失控），只有平台 admin 可以。

### 6. 批量导入学生

进入「学校 → 学生管理」→「批量导入」上传 Excel。

**Excel 必填列**：身份证号、姓名、班级 ID（如 \`2024-0001-0001\`）。
**可选列**：学号、性别、电话、家长电话、家长姓名、备注。

**初始密码**：身份证号后 6 位。

> 同身份证已存在 → 系统自动判定为升学 / 转班 / 转校，**只创建新的就读记录，不重建学生**。详见「学生升学 / 转校」章节。

## 验证

完成后：

- 校管理员登录用户端 → 看到「学校管理」菜单 → 学生 / 班级 / 教师 / 危机预警都能看到
- 班主任登录用户端 → 看到「班级数据」菜单 → 只能看到自己班级
- 普通任课老师登录用户端 → 不显示「班级数据」/「学校管理」（与学生体验一致，只能答自己被推送的问卷）

`.trim(),
  },
  {
    id: 'workflow-questionnaire-lifecycle',
    title: '工作流 2：问卷完整生命周期',
    group: '集成工作流',
    content: `
# 工作流 2：问卷完整生命周期

从配置到收集到查看报告，一份问卷的全过程。**只有平台管理员（admin）能配置问卷模板和指标**，校管理员仅能查看数据。

## 步骤

### 1. 创建问卷

进入「问卷管理」→「新建」，填写：

- **标题、说明**
- **学校**（可选，留空则面向全平台）
- **学年**（用于按学年归档）
- **是否需要知情同意书**（敏感问卷推荐开启）

### 2. 加题

进入问卷详情 → 「题目」tab，逐题添加或从「题库」导入。

题型：单选、多选、文本、滑块、矩阵、说明文本、填空、动态填空、日期、短文本、下拉、音频录制、自我图式。

每题可设置：
- 得分（评分用）
- 是否必填
- 显示逻辑（基于其他题目的答案，跳过当前题）
- 分页前断（开启分页模式时使用）

### 3. 配指标 + 危机规则

「指标」tab → 新增指标。

- **公式**：SUM / AVERAGE / WEIGHTED_SUM
- **结论规则**：按分数区间 / 文本等于 / 表达式 等条件，匹配返回不同结论
- **危机预警标记**：勾选某条结论的「危机预警」，命中该结论的学生会进入预警列表

### 4. 大报告模板

「报告模板」tab →「编辑模板」。

- 富文本编辑器（支持从 Word 复制粘贴）
- 在内容中插入「占位符」：参与人数、指标均分、结论分布饼图等
- 实际数据在「查看报告」时才会渲染

### 5. 发布 + 推送

发布后才能推送。「问卷管理」列表 → 「推送」按钮：

- 选择目标学校 / 班级 / 角色
- 设置开始 / 结束时间

### 6. 监控 + 查看报告

「问卷管理」→「统计」按钮 → 进入统计页：

- **指标统计 tab**：题目分布、指标分布、班级对比
- **大报告 tab**：渲染配置好的模板，可按学校 / 班级筛选 scope

「危机预警」菜单 → 看危机名单。

> 老师视角：班主任在「班级数据」里看到自己班的指标统计，校管理员可以从「学校管理 → 学校管理员」入口看本校。
`.trim(),
  },
  {
    id: 'workflow-promotion',
    title: '工作流 3：学生升学 / 转校',
    group: '集成工作流',
    content: `
# 工作流 3：学生升学 / 转校

**核心机制**：学生表只是身份信息，**学生与班级是多对多关系**（通过就读记录 \`enrollment\` 表）。
每条 enrollment 包含：班级、学年、是否当前。学生可以有多条历史 enrollment，方便回溯。

## 同校升学（最常见）

例：高一1班学生升入高二1班。

### 操作步骤

1. **学校开学初**：先批量创建好新学年班级（工作流 1 第 3 步）
2. **批量导入**：用同一个学生导入功能，上传 Excel（必须包含**新学年的班级 ID**）
3. **系统自动**：
   - 识别身份证已存在 → 标记为「关联已有学生」
   - **只创建新的 enrollment 记录**，旧 enrollment 自动设为「非当前」
   - 学生本人和历史问卷数据全部保留

### 注意事项

- 一个学生**同一学年不能在两个不同班级**（系统会拒绝）
- Excel 里的 \`classId\` 必须是新学年的（如 \`2025-0001-0001\`），不是旧学年的

## 跨校转学

例：A 校学生转到 B 校。

### 前提

- B 校的管理员先把该学生未来要进的班级建好
- 该学生的身份证号在系统中已存在（从 A 校导入过）

### 操作步骤

1. **B 校管理员**用学生导入功能上传 Excel
2. Excel 中 \`classId\` 用 B 校的班级 ID
3. 系统自动建新 enrollment，旧 A 校 enrollment 设非当前

### 历史报告归属

**关键设计**：学生答题完成（提交）那一刻，系统会**冻结**学生当时的学校 / 年级 / 班级到答案表里（snapshot）。

- 学生升学 / 转校后查询历史报告，仍按**提交时归属**统计
- 例如 2024 学年高一1班的报告，永远显示当时的 25 个学生，不会因为他们升入高二而变化

## 单个学生升学（手工）

进入学生详情页 →「就读历史」→ 「新增就读记录」，手动指定新班级。等价于 import 一行。

## 常见错误

| 错误信息 | 原因 | 解决 |
|---|---|---|
| "该学生在 2024 学年已关联班级 ..." | 一个学生一学年只能关联一个班 | 先「移出」旧班级再加新班级 |
| "班级不存在" | Excel 里的 classId 错了 | 检查班级是否已建好 |
| "身份证号码已存在" | 不是导入场景，是单条创建 | 改用「关联已有学生」功能 |
`.trim(),
  },
  {
    id: 'workflow-roles',
    title: '工作流 4：四种角色与权限',
    group: '集成工作流',
    content: `
# 工作流 4：四种角色与权限

平台一共 4 种角色，各自能做什么 / 不能做什么：

## 1. 平台管理员（admin）

- 登录 admin 站点
- **所有功能**：跨学校管理 + 配置问卷 + 看所有数据
- 通常 1-2 人，平台运维方

## 2. 校管理员（学校层面，可多人）

- 登录用户端 → 多出「学校管理」菜单
- **只能管自己学校**（schoolId 锁死）
- 能做：建/改/删学生 + 班级 + 老师，看学校所有问卷数据 / 大报告 / 危机预警
- **不能**：改"校管理员"标记（防权限滥用）、配置问卷模板 / 指标 / 大报告模板

## 3. 班主任（带至少 1 个班的老师）

- 登录用户端 → 「班级数据」菜单
- **只能看自己 \`teacher_class_permissions\` 里登记的班级**
- 能做：看自己班的问卷数据、班级大报告、班级危机预警
- **不能**：改学生信息（这是校管理员的活）、看其他班、看学校全局

## 4. 普通任课老师

- 登录用户端 → **没有任何统计/管理菜单**（与学生体验一致）
- 能做：完成自己被推送的问卷
- **不能**：看任何学校 / 班级 / 学生数据

## 角色不互斥

一个老师**可以同时**是「校管理员 + 班主任」，权限取并集（实际就是校管理员，因为校管理员已覆盖全校）。

## 怎么设置

### 设校管理员

平台管理员进入「学校 → 教师管理」→ 编辑老师 → 「校管理员」开关。

### 设班主任

两种方式（推荐用第 1 种）：

1. **班级表单**：编辑班级 → 「班主任」搜索框选老师 → 自动同步权限
2. **教师表单**：编辑老师 → 「班级权限」勾选要管的班

### 取消班主任

- 编辑班级 → 「班主任」字段清空 → 旧班主任的该班权限自动撤销
- 或在教师权限页面取消勾选

> 删除班级时，该班所有老师的权限自动清理（防孤立 permission）。
`.trim(),
  },

  // ==================== 核心概念 ====================
  {
    id: 'concept-roles',
    title: '角色',
    group: '核心概念',
    content: `
# 角色

平台用户分两大类：

- **管理员（admin）**：在 admin 站点登录，平台运营方
- **教师 + 学生**：在用户端登录

教师下分 3 个子角色（详见工作流 4）：
1. **校管理员**：本校全权限
2. **班主任**：自己班权限
3. **任课老师**：只能答问卷

判断老师子角色的依据：
- \`teacher.is_school_admin = true\` → 校管理员
- 否则查 \`teacher_class_permissions\`：有 ≥ 1 条 → 班主任，否则 → 任课老师
`.trim(),
  },
  {
    id: 'concept-snapshot',
    title: 'Snapshot：跨学年报告原理',
    group: '核心概念',
    content: `
# Snapshot：跨学年报告原理

## 设计目标

学生升学 / 转班 / 转校后，查看历史报告时，**应该按答题那一刻的归属聚合**，不能跟着学生当前所在班级走。

## 实现

\`answers\` 表上有 5 个 snapshot 列，在学生**点「提交」时**冻结：

- \`school_id_snapshot\`
- \`school_name_snapshot\`
- \`grade_snapshot\`
- \`class_id_snapshot\`
- \`class_name_snapshot\`

后续学生班级 / 学校怎么变，都不影响这些字段。

## 报告查询时

按 \`?schoolId=&classId=\` query 过滤的就是这些 snapshot 列：

- 不传 → 全量
- 仅 schoolId → 该校所有班的汇总（基于答题时归属）
- schoolId + classId → 该班级（基于答题时归属）

## 注意

- 教师角色看报告时，scope 自动按权限收紧（校管理员限本校，班主任限自己班）
- 学生答题中途换班是极端情况：snapshot 仅在 \`complete\` 时锁定，未完成的答题不会被锁
`.trim(),
  },
  {
    id: 'concept-indicator',
    title: '指标 / 公式 / 结论规则',
    group: '核心概念',
    content: `
# 指标 / 公式 / 结论规则

一份问卷可配置多个指标。每个指标包括：

## 计算公式

- **SUM**：题分相加
- **AVERAGE**：题分平均
- **WEIGHTED_SUM**：每题带权重，加权相加

## 结论规则

按 \`orderNum\` 顺序匹配，**第一个命中**的结论生效：

- **SCORE_RANGE**：分数区间（min/max + 是否包含端点）
- **SCORE_EQUALS**：分数等于某值
- **TEXT_EQUALS**：文本题答案等于某值（如自由文本"是/否"分支）
- **EXPRESSION**：复杂逻辑表达式（如 \`Q1>50 && Q2<30\`）

## 危机预警

某条结论勾选「危机预警」，命中后该学生进入「危机预警」名单（admin / 校管理员 / 该班班主任都能看到）。

## 配色

每条结论可设颜色（如严重 = 红色，正常 = 绿色），用于报告中的饼图 / 柱图。
`.trim(),
  },
  {
    id: 'concept-report-template',
    title: '大报告模板与占位符',
    group: '核心概念',
    content: `
# 大报告模板与占位符

## 模板

类似 Word：HTML 富文本，支持 Word 复制粘贴。

## 占位符

在内容里插入"动态值"，渲染时根据实际数据替换。

### 占位符类型

| 类型 | 示例 | 说明 |
|---|---|---|
| **系统指标** | 参与人数、应答率 | RESPONDENT_COUNT / DISTRIBUTED_COUNT / COMPLETION_RATE |
| **问卷指标** | 平均分、最高最低、结论分布 | 关联具体指标 |
| **结论级别人数 / 占比** | "情绪严重 18 人 (18%)" | 关联指标 + 具体结论规则 |
| **图表** | 饼图、柱图 | 结论分布可视化 |
| **自定义计算** | 表达式 | 例如 \`(参与人数 / 应答数) * 100\` |

## 渲染时

- 在「问卷管理 → 统计页 → 大报告 tab」查看
- 校管理员/班主任在「学校管理」/「班级数据」菜单下查看（自动按权限收紧 scope）
`.trim(),
  },

  // ==================== 常见问题 ====================
  {
    id: 'faq-students-no-questionnaire',
    title: '学生登录后看不到问卷？',
    group: '常见问题',
    content: `
# 学生登录后看不到问卷？

可能原因：

1. **问卷未发布**：进入问卷管理确认状态是「已发布」
2. **没有推送**：问卷发布只是上线模板，必须再「推送」给具体学生才会进入学生列表
3. **推送时间未到 / 已过期**：检查推送的开始 / 结束时间
4. **学生不在推送的班级范围内**：检查推送时选了哪些班级
5. **学生已答完**：已完成的问卷在「我的问卷」→「已完成」tab
`.trim(),
  },
  {
    id: 'faq-teacher-cant-see-stats',
    title: '老师登录后看不到「班级数据」菜单？',
    group: '常见问题',
    content: `
# 老师登录后看不到「班级数据」菜单？

老师默认是「普通任课老师」，必须给他权限：

- 校管理员：在 admin 端「教师管理」编辑老师 → 「校管理员」开关
- 班主任：在 admin 或用户端「班级管理」编辑班级 → 「班主任」选这位老师

设置后**老师重新登录**才能看到菜单（前端从 \`teacherRole\` 字段判断）。
`.trim(),
  },
  {
    id: 'faq-import-failed',
    title: '批量导入 Excel 失败 / 部分行跳过？',
    group: '常见问题',
    content: `
# 批量导入 Excel 失败 / 部分行跳过？

## 通用规则

- **必填列缺失**：身份证号、姓名 必填
- **身份证号格式不对**：必须 18 位
- **身份证号已存在**：
  - 学生：标记为 ASSOCIATED → 自动加新 enrollment（升学场景）
  - 老师：跳过，不覆盖

## 学生导入特殊

- **班级 ID 不存在**：检查班级 ID 是否拼对（格式 \`2024-0001-0001\`）
- **同班级同学号已被使用**：班级 + 学号是唯一约束

## 老师导入特殊

- **校管理员列**：填 "是 / 否 / Y / N"，留空表示不是校管理员
- **班级列**：用 \`/\` \`,\` \`;\` 分隔多个班级 ID

## 部分行成功 / 部分失败

支持。列表里会显示每行状态（成功 / 跳过 / 失败 + 错误原因），不会因为 1 行错误回滚整批。
`.trim(),
  },

  // ==================== 参考 ====================
  {
    id: 'reference-pdf',
    title: '完整 PDF 操作手册',
    group: '参考',
    content: `
# 完整 PDF 操作手册

需要详细图文步骤的，可下载或在线查看：

- [📄 下载 PDF](/manuals/admin-manual.pdf)
- [🌐 网页版查看](/manuals/admin-manual.html){target="_blank"}

PDF 版本包含：
- 完整截图
- 详细操作步骤
- 故障排查清单
`.trim(),
  },
];

/** 默认显示的 section id */
export const DEFAULT_SECTION_ID = 'quickstart';
